import {Component, PanelLifecycleContext} from 'panel';
import produce from 'immer';

import {Bucket} from 'common/widgets/bucket-pane/types';
import {MetricEntry, QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {ComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/types';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {Segmentation} from 'common/types/reports/query';
import {ConversionWindow, CountType} from 'common/report/funnels/models/types';

import {ChartType, LIMITED_ATTRIBUTION_PROJECTS} from './report/constants';
import {BookmarkType} from './report/queries/app-api/bookmark-client';
import {getProjectId} from './report/util';
import {getOwnAttr} from './util';
import {MetricType} from './query-builder/query-block/constants';

export type AdditionalContext = Partial<MultimetricContext> &
  Partial<ViewAsFlowsContext> &
  Partial<ViewAsFunnelContext>;

export type QueryContext = {
  bookmarkType: BookmarkType;
  dataGroupId?: string;
  dateRange?: Partial<DateRange>;
  // TODO: Replace events with metrics
  metrics?: MetricEntry[];
  events?: QueryEventEntry[];
  comparedDateRange?: ComparisonDate;
  originDashboard?: DashboardOrigin;
  segments: Segment[];
  filters: PropertyFilter[];
  conversionCriteria?: ConversionCriteria;
  querySamplingEnabled?: boolean;
} & AdditionalContext;

export type SegmentationMetadata = {
  segment: Segment;
  numericBucket?: Bucket;
};

export type ConversionCriteria = {
  conversionWindow: ConversionWindow;
  countType: CountType;
  exclusionSteps: QueryEventEntry[];
  holdingPropertyConstant: Segmentation[];
};

export type MultimetricContext = {
  metricType: MetricType;
  chartType: ChartType;
};

export type ViewAsFlowsContext = {
  viewAsFlowsContext: {funnelStep: number};
};

export type ViewAsFunnelContext = {
  viewAsFunnelContext: {topPathsFlowSteps: string[]; selectedSteps: Set<number>};
};

export function isMultimetricContext(additionalContext: AdditionalContext): additionalContext is MultimetricContext {
  return !!(additionalContext as MultimetricContext)?.metricType;
}

export function isViewAsFlowsContext(additionalContext: AdditionalContext): additionalContext is ViewAsFlowsContext {
  return !!(additionalContext as ViewAsFlowsContext)?.viewAsFlowsContext;
}

export function isViewAsFunnelContext(additionalContext: AdditionalContext): additionalContext is ViewAsFunnelContext {
  return !!(additionalContext as ViewAsFunnelContext)?.viewAsFunnelContext;
}

export interface QueryContextSupport {
  updateLastReportQueryContext(queryContext: QueryContext);
  getReportQueryContext(): QueryContext;
}

export class QueryContextManager implements PanelLifecycleContext {
  private connectedComponents: Set<Component<any>> = new Set();
  public isDirty = false;
  public query: QueryContext = {
    bookmarkType: null,
    dataGroupId: null,
    dateRange: new FullLengthDateRange().getAttributes(),
    events: [],
    comparedDateRange: null,
    originDashboard: null,
    segments: [],
    filters: [],
    conversionCriteria: null,
    querySamplingEnabled: false,
  };
  public segmentationMetadatas: SegmentationMetadata[] = [];

  public updateQuery(query: Partial<QueryContext>, preventUpdate = false) {
    this.isDirty = true;
    this.query = produce(this.query, (draft) => Object.assign(draft, query));

    if (preventUpdate === false) {
      this.updateComponents();
    }
  }

  public updateSegmentationMetadatas(segmentationMetadatas: SegmentationMetadata[]) {
    this.segmentationMetadatas = segmentationMetadatas;
    this.updateComponents();
  }

  public get(field: keyof QueryContext) {
    return this.query[field];
  }

  // Panel Context lifecycle methods
  public bindToComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.add(component);
  }

  public unbindFromComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.delete(component);
  }

  private updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }
  }

  /** @todo pipe project settings here */
  public getMaxQueryDays(): number | null {
    const LARGE_PROJECT_LIMIT = 30;
    const DEFAULT_PROJECT_LIMIT = 180;
    // for multi touch attribution queries, certain projects
    // need a limit on the number of days they can query
    // note that this is different from max history days we pass
    // in other components through project feature gates
    if (this.query.events?.some((entry: QueryEventEntry) => getOwnAttr(entry, `multiAttribution`))) {
      if (LIMITED_ATTRIBUTION_PROJECTS.has(getProjectId())) {
        return LARGE_PROJECT_LIMIT;
      } else {
        return DEFAULT_PROJECT_LIMIT;
      }
    }

    return null;
  }
}
